/**
 * OrganicPlanBuilder — konfigurator planu Organic (pricing).
 * User szacuje zużycie tokenów (reelsy/grafiki/AI), a karta po prawej rekomenduje
 * plan Organic (900 tok) i auto-dobiera doładowanie gdy zużycie przekracza limit.
 * Branding VP: dark, bg-card, akcent emerald (Organic). shadcn + Tailwind, lucide.
 */
import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Film,
  Image as ImageIcon,
  Bot,
  Flame,
  DollarSign,
  Sparkles,
  Check,
  Calendar,
  MessageSquare,
  BarChart3,
  PenLine,
} from "lucide-react";

const COST = { reels: 30, graphics: 55, aivideo: 3100 } as const;
const BASE_TOKENS = 900;
const PACKS = [
  { n: 0, zl: 0 },
  { n: 500, zl: 29 },
  { n: 1500, zl: 79 },
  { n: 4000, zl: 179 },
] as const;
const PLAN = { monthlyNet: 99, annualPerMoNet: 79, annualYearNet: 950 } as const;

const fmt = (n: number) => n.toLocaleString("pl-PL");

type CreationType = {
  key: string;
  label: string;
  icon: React.ReactNode;
  unlocksAiVideo?: boolean;
};

const TYPES: CreationType[] = [
  { key: "reels", label: "Reelsy z Twoich nagrań", icon: <Film className="w-4 h-4" /> },
  { key: "graphics", label: "Grafiki marketingowe", icon: <ImageIcon className="w-4 h-4" /> },
  {
    key: "aivideo",
    label: "Reelsy AI / avatar",
    icon: <Bot className="w-4 h-4" />,
    unlocksAiVideo: true,
  },
  { key: "inspo", label: "Feed Inspiracji", icon: <Flame className="w-4 h-4" /> },
  { key: "ugc", label: "UGC / reklamy produktowe", icon: <DollarSign className="w-4 h-4" /> },
  { key: "personal", label: "Do użytku prywatnego", icon: <Sparkles className="w-4 h-4" /> },
];

const PLAN_FEATURES = [
  { icon: <Flame className="w-4 h-4" />, label: "Feed Inspiracji — daily viral pod Twoją niszę" },
  { icon: <PenLine className="w-4 h-4" />, label: "Generator skryptów i hooków" },
  { icon: <ImageIcon className="w-4 h-4" />, label: "Generacja grafik AI" },
  { icon: <Calendar className="w-4 h-4" />, label: "Scheduler publikacji" },
  { icon: <MessageSquare className="w-4 h-4" />, label: "Asystent wiadomości" },
  { icon: <BarChart3 className="w-4 h-4" />, label: "Analityka" },
];

function smallestPack(gap: number) {
  if (gap <= 0) return PACKS[0];
  for (const pack of PACKS) if (pack.n >= gap) return pack;
  return PACKS[PACKS.length - 1];
}

type SliderRowProps = {
  icon: React.ReactNode;
  label: string;
  value: number;
  max: number;
  tokens: number;
  disabled?: boolean;
  onChange: (v: number) => void;
};

function SliderRow({ icon, label, value, max, tokens, disabled, onChange }: SliderRowProps) {
  const pct = (value / max) * 100;
  return (
    <div className={disabled ? "opacity-40" : ""}>
      <input
        type="range"
        min={0}
        max={max}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-1.5 appearance-none rounded-full cursor-pointer accent-emerald-500 disabled:cursor-not-allowed"
        style={{
          background: `linear-gradient(to right, rgb(16 185 129) 0%, rgb(16 185 129) ${pct}%, rgba(255,255,255,0.1) ${pct}%, rgba(255,255,255,0.1) 100%)`,
        }}
      />
      <div className="flex items-baseline gap-2 mt-2 text-sm text-white/80">
        <span className="text-white/50">{icon}</span>
        <span>{label}:</span>
        <b className="text-emerald-400 tabular-nums">{value}</b>
        <span className="ml-auto text-xs text-white/35 tabular-nums">{fmt(tokens)} tok</span>
      </div>
    </div>
  );
}

export type OrganicPlanBuilderProps = {
  onSelect?: (cycle: "monthly" | "yearly", topupTokens: number) => void;
};

export default function OrganicPlanBuilder({ onSelect }: OrganicPlanBuilderProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set(["reels", "graphics", "inspo"]));
  const [reels, setReels] = useState(20);
  const [graphics, setGraphics] = useState(15);
  const [aivideo, setAivideo] = useState(0);
  const [annual, setAnnual] = useState(true);

  const aiVideoOn = selected.has("aivideo");

  const toggleType = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      if (key === "aivideo" && next.has(key) === false) setAivideo(0);
      return next;
    });
  };

  const { usage, pack, credits } = useMemo(() => {
    const total = reels * COST.reels + graphics * COST.graphics + aivideo * COST.aivideo;
    const chosen = smallestPack(Math.max(0, total - BASE_TOKENS));
    return { usage: total, pack: chosen, credits: BASE_TOKENS + chosen.n };
  }, [reels, graphics, aivideo]);

  const usagePct = Math.min(100, Math.round((usage / credits) * 100));
  const over = usage > credits;
  const priceNow = annual ? PLAN.annualPerMoNet : PLAN.monthlyNet;
  const firstCharge = (annual ? PLAN.annualYearNet : PLAN.monthlyNet) + pack.zl;
  const saved = PLAN.monthlyNet * 12 - PLAN.annualYearNet;

  return (
    <div className="grid lg:grid-cols-[1fr_440px] gap-6 items-start">
      {/* LEFT — configurator */}
      <Card className="bg-card border-white/[0.08] p-7 space-y-7">
        <section>
          <div className="flex items-center gap-3 mb-1">
            <span className="w-6 h-6 rounded-full bg-white/[0.05] border border-white/10 grid place-items-center text-[11px] font-bold text-white/50">
              1
            </span>
            <h3 className="text-base font-bold text-white">Co chcesz tworzyć?</h3>
          </div>
          <p className="text-xs text-white/35 mb-4 pl-9">Można zaznaczyć kilka</p>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {TYPES.map((t) => {
              const on = selected.has(t.key);
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => toggleType(t.key)}
                  className={`flex items-center gap-2.5 px-3.5 py-3 rounded-xl border text-sm font-medium text-left transition ${
                    on
                      ? "border-emerald-500 bg-emerald-500/[0.08] text-white"
                      : "border-white/[0.08] bg-white/[0.02] text-white/70 hover:border-white/20"
                  }`}
                >
                  <span
                    className={`w-6 h-6 rounded-md grid place-items-center ${
                      on ? "bg-emerald-500/20 text-emerald-400" : "bg-white/[0.05] text-white/40"
                    }`}
                  >
                    {t.icon}
                  </span>
                  {t.label}
                  <span
                    className={`ml-auto w-5 h-5 rounded-md grid place-items-center text-[11px] ${
                      on ? "bg-emerald-500 text-black" : "border border-white/15 text-transparent"
                    }`}
                  >
                    <Check className="w-3 h-3" />
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="pt-6 border-t border-white/[0.06]">
          <div className="flex items-center gap-3 mb-1">
            <span className="w-6 h-6 rounded-full bg-white/[0.05] border border-white/10 grid place-items-center text-[11px] font-bold text-white/50">
              2
            </span>
            <h3 className="text-base font-bold text-white">Ile treści miesięcznie?</h3>
          </div>
          <p className="text-xs text-white/35 mb-5 pl-9">
            ≈ 30 tok / reels z montażu · ≈ 3 100 tok / reels AI · ≈ 55 tok / grafika
          </p>
          <div className="space-y-5">
            <SliderRow
              icon={<Film className="w-4 h-4" />}
              label="Reelsy z nagrań"
              value={reels}
              max={60}
              tokens={reels * COST.reels}
              onChange={setReels}
            />
            <SliderRow
              icon={<ImageIcon className="w-4 h-4" />}
              label="Grafiki"
              value={graphics}
              max={80}
              tokens={graphics * COST.graphics}
              onChange={setGraphics}
            />
            <SliderRow
              icon={<Bot className="w-4 h-4" />}
              label="Reelsy AI / avatar"
              value={aivideo}
              max={10}
              tokens={aivideo * COST.aivideo}
              disabled={!aiVideoOn}
              onChange={setAivideo}
            />
          </div>
        </section>

        <section className="pt-6 border-t border-white/[0.06]">
          <div className="flex items-center gap-3 mb-1">
            <span className="w-6 h-6 rounded-full bg-white/[0.05] border border-white/10 grid place-items-center text-[11px] font-bold text-white/50">
              3
            </span>
            <h3 className="text-base font-bold text-white">W cenie planu</h3>
          </div>
          <p className="text-xs text-white/35 mb-4 pl-9">Wszystko w zakładce Organic</p>
          <div className="flex flex-wrap gap-2">
            {PLAN_FEATURES.map((f, i) => (
              <span
                key={i}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border ${
                  i < 3
                    ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/[0.06]"
                    : "border-white/[0.08] text-white/60 bg-white/[0.02]"
                }`}
              >
                {f.icon}
                {f.label}
              </span>
            ))}
          </div>
        </section>
      </Card>

      {/* RIGHT — recommendation */}
      <Card className="bg-card border-white/[0.08] p-6 lg:sticky lg:top-6 bg-gradient-to-b from-emerald-500/[0.05] to-transparent">
        <p className="text-center text-xs text-white/40 mb-4">
          Rekomendujemy plan <b className="text-emerald-400">Organic</b>
        </p>
        <div className="flex items-center gap-2.5">
          <span className="text-2xl font-extrabold tracking-tight text-white">ORGANIC</span>
          {annual && (
            <span className="bg-pink-600 text-white text-[11px] font-extrabold px-2 py-0.5 rounded-md">
              -20%
            </span>
          )}
        </div>
        <p className="text-white/50 text-[13px] mt-1 mb-5">
          Cała zakładka Organic + Inspiracje. Dla twórców i marek osobistych.
        </p>

        <div className="flex items-center gap-2.5 bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-3.5 font-bold text-white">
          <Sparkles className="w-4 h-4 text-emerald-400" />
          <span className="text-[17px]">{fmt(credits)} tokenów / mies</span>
        </div>
        {pack.n > 0 && (
          <div className="flex items-center gap-2 mt-2 bg-emerald-500/[0.07] border border-emerald-500/25 rounded-lg px-3.5 py-2.5 text-[13px] text-white/80">
            ➕ Doładowanie <b className="text-emerald-400">{fmt(pack.n)} tok</b> · +{pack.zl} zł
            jednorazowo{over ? " (możesz potrzebować więcej)" : ""}
          </div>
        )}

        <div className="mt-5">
          <div className="flex justify-between text-[13px] mb-1.5">
            <span className="text-white/50">Oczekiwane zużycie</span>
            <span
              className={`font-bold tabular-nums ${over ? "text-pink-400" : "text-emerald-400"}`}
            >
              {fmt(usage)} / {fmt(credits)}
            </span>
          </div>
          <div className="h-2 rounded-full bg-white/[0.08] overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                over
                  ? "bg-gradient-to-r from-amber-400 to-pink-500"
                  : "bg-gradient-to-r from-emerald-400 to-emerald-600"
              }`}
              style={{ width: `${usagePct}%` }}
            />
          </div>
        </div>

        <ul className="mt-5 space-y-2.5">
          {[
            "Feed Inspiracji — codzienne viralki zza granicy pod Twoją niszę",
            "„Zrób reelsa” — z viralki prosto do Twojego scenariusza",
            "900 tokenów/mies + doładowania na życzenie",
          ].map((t, i) => (
            <li key={i} className="flex gap-2 text-[13px] text-white/85">
              <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
              {t}
            </li>
          ))}
        </ul>

        <div className="flex items-baseline gap-2.5 mt-6">
          {annual && <span className="text-pink-500 line-through text-lg font-bold">99 zł</span>}
          <span className="text-4xl font-extrabold tracking-tight text-white">{priceNow} zł</span>
          <span className="text-white/50 text-[13px]">
            {annual ? "/mies netto, płatne rocznie" : "/mies netto"}
          </span>
        </div>

        <Button
          onClick={() => onSelect?.(annual ? "yearly" : "monthly", pack.n)}
          className="w-full mt-4 h-12 text-[15px] font-extrabold bg-emerald-500 hover:bg-emerald-400 text-black"
        >
          {pack.n > 0 ? `Wybierz Organic + doładowanie (${firstCharge} zł)` : "Wybierz Organic"}
        </Button>
        {annual && (
          <p className="text-center text-white/40 text-xs mt-3">
            <b className="text-white/60">Oszczędzasz {saved} zł</b> vs miesięcznie (
            {PLAN.annualYearNet} zł/rok)
          </p>
        )}

        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-white/[0.06]">
          <button
            type="button"
            onClick={() => setAnnual((v) => !v)}
            className="flex items-center gap-2 text-[13px] text-white/60"
          >
            <span
              className={`w-9 h-5 rounded-full relative transition ${
                annual ? "bg-emerald-500" : "bg-white/15"
              }`}
            >
              <span
                className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
                  annual ? "left-[18px]" : "left-0.5"
                }`}
              />
            </span>
            Rocznie
            <span className="bg-pink-600 text-white text-[10px] font-extrabold px-1.5 py-0.5 rounded">
              -20%
            </span>
          </button>
        </div>
      </Card>
    </div>
  );
}
